import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = [["tecnica", "🛠️ Técnica de sangria"], ["doenca_painel", "🌳 Doenças de painel/tronco"], ["radicular", "🌱 Doenças radiculares"], ["foliar", "🍃 Doenças foliares"]];
const CRITERIO_POR_CAT = { tecnica: "", doenca_painel: "fitossanidade", radicular: "radicular", foliar: "doencas_foliares" };

export default function ReferenciasBiblioteca({ referencias, onChange, onVerFicha }) {
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [novaCat, setNovaCat] = useState("foliar");
  const [criadaAgora, setCriadaAgora] = useState(null);

  const iniciarEdicao = (r) => { setEditando(r.id); setForm({ ...r }); };

  const cancelarEdicao = async () => {
    if (criadaAgora && criadaAgora === editando) {
      await axios.delete(`${API}/admin/diagnostico/referencias/${criadaAgora}`).catch(() => {});
      onChange();
    }
    setCriadaAgora(null);
    setEditando(null);
  };

  const salvar = async () => {
    if (!form.titulo?.trim() || !form.resumo?.trim()) { toast.error("Título e resumo são obrigatórios"); return; }
    setSalvando(true);
    try {
      await axios.put(`${API}/admin/diagnostico/referencias/${editando}`, {
        categoria: form.categoria, criterio: form.criterio || "", titulo: form.titulo,
        resumo: form.resumo, texto: form.texto || "", fonte: form.fonte || "", imagem_url: form.imagem_url || "",
      });
      toast.success("Ficha atualizada — a IA passa a usar o novo conteúdo");
      setEditando(null); setCriadaAgora(null); onChange();
    } catch { toast.error("Erro ao salvar ficha"); }
    finally { setSalvando(false); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir esta ficha da biblioteca?")) return;
    try { await axios.delete(`${API}/admin/diagnostico/referencias/${id}`); toast.success("Ficha excluída"); onChange(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const criarNova = async () => {
    try {
      const r = await axios.post(`${API}/admin/diagnostico/referencias`, {
        categoria: novaCat, criterio: CRITERIO_POR_CAT[novaCat] || "", titulo: "Nova ficha",
        resumo: "", texto: "", fonte: "", imagem_url: "",
      });
      onChange();
      setCriadaAgora(r.data.id);
      iniciarEdicao(r.data);
      toast.success("Ficha criada — preencha o conteúdo");
    } catch { toast.error("Erro ao criar ficha"); }
  };

  return (
    <div className="space-y-3" data-testid="referencias-biblioteca">
      {CATS.map(([cat, label]) => {
        const items = referencias.filter((r) => r.categoria === cat);
        if (!items.length) return null;
        return (
          <div key={cat}>
            <div className="text-xs font-bold text-gray-600 mb-1">{label} ({items.length})</div>
            <div className="space-y-2">
              {items.map((r) => (
                <div key={r.id} className="border rounded-lg p-2" data-testid={`ref-item-${r.id}`}>
                  {editando === r.id ? (
                    <div className="space-y-1.5">
                      <input value={form.titulo || ""} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className="w-full border rounded p-1.5 text-xs font-semibold" placeholder="Título" data-testid="ref-edit-titulo" />
                      <textarea value={form.resumo || ""} onChange={(e) => setForm({ ...form, resumo: e.target.value })} rows={3} className="w-full border rounded p-1.5 text-xs" placeholder="Resumo (é o que a IA usa no contraste)" data-testid="ref-edit-resumo" />
                      <textarea value={form.texto || ""} onChange={(e) => setForm({ ...form, texto: e.target.value })} rows={4} className="w-full border rounded p-1.5 text-xs" placeholder="Texto completo da ficha (exibido ao abrir)" data-testid="ref-edit-texto" />
                      <input value={form.fonte || ""} onChange={(e) => setForm({ ...form, fonte: e.target.value })} className="w-full border rounded p-1.5 text-xs" placeholder="Fonte (ex.: Embrapa)" data-testid="ref-edit-fonte" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={salvar} disabled={salvando} className="bg-green-700 hover:bg-green-800 text-xs h-8" data-testid="ref-salvar-btn">{salvando ? "..." : "💾 Salvar"}</Button>
                        <Button size="sm" variant="outline" onClick={cancelarEdicao} className="text-xs h-8" data-testid="ref-cancelar-btn">Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {r.imagem_url && <img src={r.imagem_url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0 cursor-pointer" onClick={() => onVerFicha(r)} />}
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onVerFicha(r)}>
                        <div className="text-xs font-semibold truncate">{r.titulo}</div>
                        <div className="text-[10px] text-gray-500 truncate">{r.fonte || "sem fonte"}</div>
                      </div>
                      <button onClick={() => onVerFicha(r)} className="text-sm px-1.5" title="Ver ficha" data-testid={`ref-ver-${r.id}`}>📖</button>
                      <button onClick={() => iniciarEdicao(r)} className="text-sm px-1.5" title="Editar" data-testid={`ref-editar-${r.id}`}>✏️</button>
                      <button onClick={() => excluir(r.id)} className="text-red-500 text-sm px-1" title="Excluir" data-testid={`ref-excluir-${r.id}`}>🗑️</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex gap-2 items-center pt-1 border-t">
        <select value={novaCat} onChange={(e) => setNovaCat(e.target.value)} className="border rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="ref-nova-cat">
          {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={criarNova} className="text-xs h-8 border-green-600 text-green-700" data-testid="ref-nova-btn">➕ Nova ficha</Button>
      </div>
    </div>
  );
}
